// src/contexts/WishlistContext.tsx

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import api from "api"; // ✅ Axios instance with baseURL and interceptors
import { Product } from "data/data";
import { useAuth } from "api/AuthContext";

export type WishlistContextType = {
  wishlist: Product[];
  toggleWishlist: (product: Product) => void;
  isInWishlist: (productId: number) => boolean;
};

const WishlistContext = createContext<WishlistContextType | undefined>(
  undefined
);

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const [wishlist, setWishlist] = useState<Product[]>([]);

  const fetchWishlist = async () => {
    if (!user) return; // ✅ Skip if not authenticated

    try {
      const res = await api.get("/savedlists");
      const products: Product[] = res.data.map((item: any) => item.Product);
      setWishlist(products);
    } catch (err) {
      console.error("Failed to fetch wishlist:", err);
    }
  };

  const toggleWishlist = async (product: Product) => {
    if (!user) return; // ✅ Skip toggle if not authenticated

    const isSaved = wishlist.some((p) => p.id === product.id);

    try {
      if (isSaved) {
        await api.delete(`/savedlists/${product.id}`);
        setWishlist((prev) => prev.filter((p) => p.id !== product.id));
      } else {
        await api.post("/savedlists", { productId: product.id });
        setWishlist((prev) => [...prev, product]);
      }
    } catch (err) {
      console.error("Failed to toggle wishlist:", err);
    }
  };

  const isInWishlist = (productId: number) =>
    wishlist.some((item) => item.id === productId);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setWishlist([]);
      return;
    }
    fetchWishlist();
  }, [authLoading, user]);

  return (
    <WishlistContext.Provider
      value={{ wishlist, toggleWishlist, isInWishlist }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};
